'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Control, Controller, useForm } from 'react-hook-form';
import useSwrInfinite from 'swr/infinite';
import { VideosLoader } from '@/app_components/Conferences/videos/VideosLoader';
import { Icon } from '@/app_components/common/Icon';
import { SpinningLoader } from '@/app_components/icons/SpinningLoader';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { SelectV2 } from '@/app_components/ui/SelectV2';
import { VideoDefaultLoader } from '@/app_components/Video/loaders/VideoDefaultLoader';
import { VideoDefault } from '@/app_components/Video/VideoDefault';
import { VideoModalVod } from '@/app_components/Video/VideoModalVod';
import { Breakpoints, useMediaQuery } from '@/app_lib/hooks/useMediaQuery';
import type { VodVideo, VodVideoPlaylist } from '@/app_lib/types/Video';
import type { Option } from '@/app_lib/utils/dropdownCheckboxUtils';
import { conferencesPlaylistOptions } from '@/app_lib/utils/pages/conferences/constants';
import { convertConferencesVodVideoToVideo } from '@/app_lib/utils/pages/conferences/utils';
import { cn } from '@/app_lib/utils/utils';

const CONFERENCES_VIDEOS_PAGE_MOBILE = 4;
const CONFERENCES_VIDEOS_PAGE_DESKTOP = 16;

function getPageSize(isMobile: boolean | undefined) {
  if (isMobile === undefined) {
    return;
  }
  if (isMobile) {
    return CONFERENCES_VIDEOS_PAGE_MOBILE;
  }
  return CONFERENCES_VIDEOS_PAGE_DESKTOP;
}

function getPlaylistKey(
  pageIndex: number,
  previousPageData: VodVideoPlaylist | null,
  pageSize: number | undefined,
  slug: string,
  createdAt?: string | null,
) {
  if (pageSize === undefined) {
    return null;
  }

  const nextFrom = pageIndex * pageSize;
  if (previousPageData) {
    const batch = previousPageData.videosOrderedByUser;
    const noMoreInBatch = batch.length === 0 || batch.length < pageSize;
    const pastTotal = nextFrom >= previousPageData.totalVideos;
    if (noMoreInBatch || pastTotal) {
      return null;
    }
  }

  const params = new URLSearchParams({
    from: String(nextFrom),
    size: String(pageSize),
    slug: conferencesPlaylistOptions.find((o) => o.value === slug)?.label ?? conferencesPlaylistOptions[0].label,
  });
  if (createdAt && createdAt !== 'all-years') {
    params.set('createdAt', createdAt);
  }
  return `/api/conferences/getPlaylist?${params}`;
}

async function getPlaylist(url: string): Promise<VodVideoPlaylist> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Failed to load conferences playlist');
  }
  return response.json() as Promise<VodVideoPlaylist>;
}

function updateSearchParams(params: URLSearchParams) {
  const query = params.toString();
  const newUrl = query ? `?${query}` : window.location.pathname;
  window.history.replaceState(null, '', newUrl);
}

const NoVideos = ({ error, onClearFilters }: { error: Error | null; onClearFilters: () => void }) => (
  <div className='flex flex-col layout-gap-md'>
    <div
      className='col-span-full flex flex-col items-center justify-center h-[450px] w-full layout-gap-xs'
      id='no-events'
    >
      <Icon
        className='text-blue-1'
        icon='NoVideos'
        size='xl'
      />
      <div className='flex flex-col gap-sm items-center justify-center text-center'>
        <span className='typography-level-2 font-semibold'>
          {error ? 'Something went wrong loading playlist.' : 'No videos match your filters.'}
        </span>
        <span className='text-secondary'>
          {error
            ? 'Please try again later or try another topic/playlist or year.'
            : 'Try another topic/playlist or year.'}
        </span>
      </div>
      <ButtonV2
        as='button'
        className='typography-sm'
        noCapitalize
        onClick={onClearFilters}
        variant='secondaryOutline'
      >
        <span>Clear filters</span>
      </ButtonV2>
    </div>
  </div>
);

type FormValues = {
  topic: string;
  year: string;
};

type PatchUrlParams = (patch: (params: URLSearchParams) => void) => void;

const ConferencesVideosFilters = ({
  control,
  years,
  isPlaylistBusy,
  patchUrlParams,
  playlistLabel,
  showTitlePlaceholder,
}: {
  control: Control<FormValues>;
  years: Option[];
  isPlaylistBusy: boolean;
  patchUrlParams: PatchUrlParams;
  playlistLabel: string;
  showTitlePlaceholder: boolean;
}) => (
  <form className='flex justify-between items-center'>
    <h2
      className={cn(
        'typography-level-4 font-graphik font-medium',
        showTitlePlaceholder && 'bg-helper text-transparent animate-pulse',
      )}
    >
      {showTitlePlaceholder ? 'All Videos' : playlistLabel}
    </h2>
    <div className='flex gap-sm'>
      <Controller
        control={control}
        name='topic'
        render={({ field }) => (
          <SelectV2
            className={cn(isPlaylistBusy && '[&>button]:button-tertiary-accent-disabled')}
            disabled={isPlaylistBusy}
            id='topic'
            label='Topic'
            options={conferencesPlaylistOptions}
            trigger={<span className='font-medium'>Topic</span>}
            {...field}
            onChange={(_, value) => {
              field.onChange(value);
              patchUrlParams((p) => p.set('tag', value));
            }}
          />
        )}
      />
      <Controller
        control={control}
        name='year'
        render={({ field }) => (
          <SelectV2
            className={cn(isPlaylistBusy && '[&>button]:button-tertiary-accent-disabled')}
            disabled={isPlaylistBusy}
            id='year'
            label='Year'
            options={years}
            trigger={<span className='font-medium'>Year</span>}
            {...field}
            onChange={(_, value) => {
              field.onChange(value);
              patchUrlParams((p) => {
                if (value === 'all-years') {
                  p.delete('createdAt');
                } else {
                  p.set('createdAt', value);
                }
              });
            }}
          />
        )}
      />
    </div>
  </form>
);

export const ConferencesVideos = ({ years, topics }: { years: Option[]; topics: Option[] }) => {
  const searchParams = useSearchParams();
  const isMobile = useMediaQuery(Breakpoints.Mobile);
  const pageSize = getPageSize(isMobile);
  const [forceOpen, setForceOpen] = useState(Boolean(searchParams?.get('watch')));
  const videosLengthBeforeLoadMoreRef = useRef<number | null>(null);
  const [noMoreVideos, setNoMoreVideos] = useState(false);

  const defaultValues = useMemo(
    () => ({
      topic: searchParams?.get('tag') || topics[0].value,
      year: searchParams?.get('createdAt') ?? years[0].value,
    }),
    [searchParams, topics, years],
  );
  const form = useForm<FormValues>({
    defaultValues,
  });

  const { control, watch } = form;

  const playlist = watch('topic');
  const createdAt = watch('year');

  useEffect(() => {
    setNoMoreVideos(false);
    videosLengthBeforeLoadMoreRef.current = null;
  }, []);

  const { data, error, isLoading, isValidating, setSize, size } = useSwrInfinite<VodVideoPlaylist>(
    (pageIndex, previousPageData) => getPlaylistKey(pageIndex, previousPageData, pageSize, playlist, createdAt),
    getPlaylist,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    },
  );

  const videos = useMemo(
    () => (data ?? []).flatMap((page) => page.videosOrderedByUser.map(convertConferencesVodVideoToVideo)),
    [data],
  );

  useEffect(() => {
    if (isValidating || videosLengthBeforeLoadMoreRef.current === null) {
      setNoMoreVideos(false);
      return;
    }
    const before = videosLengthBeforeLoadMoreRef.current;
    videosLengthBeforeLoadMoreRef.current = null;
    if (videos.length === before) {
      setNoMoreVideos(true);
    }
  }, [isValidating, videos.length]);

  const getVideos = useCallback(
    async ({ index }) => {
      const videoWatch = searchParams?.get('watch');
      if (videoWatch) {
        const video = await fetch(`/api/conferences/getVideo?videoId=${videoWatch}`);
        const json = (await video.json()) as VodVideo;
        return [convertConferencesVodVideoToVideo(json), ...videos.filter((v) => v.id !== videoWatch).slice(0, 4)];
      }
      const videoId = videos[index].id;
      const params = new URLSearchParams(searchParams?.toString());
      params.append('watch', videoId);

      updateSearchParams(params);
      return [videos[index], ...videos.filter((v) => v.id !== videoId).slice(0, 4)];
    },
    [searchParams, videos],
  );

  const totalVideos = data?.[0]?.totalVideos ?? 0;

  const patchUrlParams = useCallback(
    (patch: (params: URLSearchParams) => void) => {
      const params = new URLSearchParams(searchParams?.toString());
      patch(params);
      updateSearchParams(params);
      setSize(1);
    },
    [searchParams, setSize],
  );

  const loadMore = () => {
    videosLengthBeforeLoadMoreRef.current = videos.length;
    void setSize(size + 1);
  };

  const videoModalTarget = `video-modal-${playlist}`;

  const isPlaylistBusy = isLoading || isValidating;
  const isInitialLoading = pageSize === undefined || (isLoading && !data);
  const showTitlePlaceholder = isPlaylistBusy || isInitialLoading;

  const skeletonCount = useMemo(() => {
    const effectivePageSize = pageSize ?? CONFERENCES_VIDEOS_PAGE_DESKTOP;
    if (totalVideos <= 0) {
      return effectivePageSize;
    }
    return Math.min(effectivePageSize, totalVideos - videos.length);
  }, [pageSize, totalVideos, videos.length]);

  const hasVideos = videos.length > 0 && !error;
  const playlistLabel = useMemo(
    () =>
      conferencesPlaylistOptions.find((o) => o.value === (playlist || searchParams?.get('tag')))?.label ||
      conferencesPlaylistOptions[0].label,
    [playlist, searchParams],
  );

  return (
    <div className='flex flex-col layout-gap-xs'>
      <ConferencesVideosFilters
        control={control}
        isPlaylistBusy={isPlaylistBusy}
        patchUrlParams={patchUrlParams}
        playlistLabel={playlistLabel}
        showTitlePlaceholder={showTitlePlaceholder}
        years={years}
      />
      {isInitialLoading && <VideosLoader skeletonCount={skeletonCount} />}
      {hasVideos && (
        <>
          <div className='flex flex-col layout-gap-md'>
            <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 layout-grid-gap'>
              {videos.map((video, index) => (
                <VideoDefault
                  className='fade-in-fast'
                  data-index={index}
                  key={`${videoModalTarget}-${index}-${video.id}`}
                  modal={false}
                  orientation='vertical'
                  target={videoModalTarget}
                  video={video}
                  videoType='vod'
                />
              ))}
              {isValidating &&
                !isInitialLoading &&
                videos.length !== size * pageSize &&
                Array.from({ length: skeletonCount }, (_, index) => (
                  <VideoDefaultLoader
                    // biome-ignore lint/suspicious/noArrayIndexKey: This is a fake key
                    key={`video-loader-${index}`}
                    modal={false}
                    orientation='vertical'
                  />
                ))}
              <VideoModalVod
                forceOpen={forceOpen}
                getVideos={getVideos}
                onClose={() => {
                  setForceOpen(false);
                  patchUrlParams((p) => p.delete('watch'));
                }}
                target={videoModalTarget}
              />
            </div>
          </div>
          <div className={cn('flex flex-col items-center gap-lg justify-center', noMoreVideos && 'hidden')}>
            <span className='typography-sm text-secondary'>
              1-{videos.length} of {totalVideos}
            </span>
            {videos.length < totalVideos && (
              <ButtonV2
                as='button'
                disabled={isValidating}
                noCapitalize
                onClick={loadMore}
                size='lg'
                variant='secondaryOutline'
              >
                <span className='typography-sm'>See more videos</span>
                {isValidating ? (
                  <SpinningLoader size={16} />
                ) : (
                  <Icon
                    icon='ButtonDown'
                    size='custom'
                  />
                )}
              </ButtonV2>
            )}
          </div>
        </>
      )}
      {!(hasVideos || isInitialLoading) && (
        <NoVideos
          error={error}
          onClearFilters={() => {
            updateSearchParams(new URLSearchParams());
            setSize(1);
            form.reset({ topic: topics[0].value, year: years[0].value });
          }}
        />
      )}
    </div>
  );
};
